import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useParams, useHistory, Redirect } from 'react-router-dom';
import { Button, Label, Grid } from 'semantic-ui-react';
import { PageContainer } from '../../components/pageContainer/PageContainer';
import { Panel } from '../../components/panel/Panel';
import { useAppStateFeedSelector } from './feedSlice';
import { loadPostDetail } from './feedActionCreators';
import { FeedCard } from './components/FeedCard';
import { MediaGallery } from './components/MediaGallery';
import { FeedLoader } from './components/FeedLoader';
import { FeedUnavailable } from './components/FeedUnavailable';
import { useFeatureFlag, useFeatureFlagsSettled, useFeatureFlagsErrored } from '../featureFlags/FeatureFlag';
import { isVideoMedia } from '../../models/feed';
import { getContrastTextColor } from '../../utils/getContrastTextColor';
import styles from './PostDetail.module.scss';
import moment from 'moment';
import elginLogo from '../../static/img/elgin-logo-white.png';

export const PostDetail: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const { id } = useParams<{ id: string }>();
    const newsAndEventsFF = useFeatureFlag('EnableNewsAndEventsFeed');
    const featureFlagsSettled = useFeatureFlagsSettled();
    const featureFlagsErrored = useFeatureFlagsErrored();

    const { currentPost, relatedPosts, detailLoading } = useAppStateFeedSelector(
        (state) => state
    );

    useEffect(() => {
        if (id) {
            dispatch(loadPostDetail(id));
        }
    }, [dispatch, id]);

    // Only redirect if we've finished loading and still don't have a post
    // Don't redirect on initial mount when loading hasn't started yet
    const [hasAttemptedLoad, setHasAttemptedLoad] = React.useState(false);

    useEffect(() => {
        if (detailLoading) {
            setHasAttemptedLoad(true);
        }
    }, [detailLoading]);

    useEffect(() => {
        if (!currentPost && !detailLoading && hasAttemptedLoad && id) {
            // Post not found after attempting to load
            setTimeout(() => {
                history.push('/');
            }, 2000);
        }
    }, [currentPost, detailLoading, hasAttemptedLoad, id, history]);

    // Determine if we should show a gallery or single image
    const hasMultipleMedia = currentPost?.images && currentPost.images.length > 1;
    const hasSingleMedia = currentPost?.images && currentPost.images.length === 1;
    const firstImage = currentPost?.images?.[0];
    const hasVideoContent = firstImage && isVideoMedia(firstImage.mediaType);
    const postImageUrl = currentPost?.imageUrl;

    // Get display image URL for single image display - must be before early returns
    const displayImageUrl = useMemo(() => {
        if (hasVideoContent && firstImage?.thumbnailUrl) {
            return firstImage.thumbnailUrl;
        }
        return postImageUrl;
    }, [hasVideoContent, firstImage, postImageUrl]);

    // Precedence matters, and each gate is separate on purpose:
    //   1. flags unresolved -> hold the route (an unresolved flag is not a disabled one)
    //   2. flags say disabled -> bounce immediately, without waiting on the post fetch
    //   3. post still loading -> loader
    // Folding 1 and 3 together would make a disabled feed sit on a spinner for the
    // duration of a detail request it should never have waited for.
    if (!featureFlagsSettled) {
        return <FeedLoader variant="post" label={t('Feed.Loading')} />;
    }

    if (featureFlagsErrored) {
        return <FeedUnavailable />;
    }

    if (!newsAndEventsFF?.enabled) {
        return <Redirect to="/" />;
    }

    if (detailLoading) {
        return <FeedLoader variant="post" label={t('Feed.Loading')} />;
    }

    if (!currentPost) {
        return (
            <PageContainer>
                <Panel>
                    <div className={styles.errorState}>
                        <h2>{t('Feed.PostNotFoundTitle')}</h2>
                        <p>{t('Feed.PostNotFoundBody')}</p>
                    </div>
                </Panel>
            </PageContainer>
        );
    }

    const handleShare = () => {
        const url = window.location.href;
        navigator.clipboard.writeText(url);
        alert(t('Feed.LinkCopied'));
    };

    const getNextEventDate = () => {
        if (currentPost.type !== 'event' || !currentPost.eventDates || currentPost.eventDates.length === 0) {
            return null;
        }

        const now = new Date();
        const futureDates = currentPost.eventDates.filter(
            (date) => new Date(date.eventDate) >= now
        );

        if (futureDates.length === 0) {
            return currentPost.eventDates[0];
        }

        return futureDates.sort(
            (a, b) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime()
        )[0];
    };

    const nextEventDate = getNextEventDate();

    return (
        <PageContainer>
            <Panel>
                <Button
                    basic
                    icon="arrow left"
                    content={t('Feed.Back')}
                    onClick={() => history.goBack()}
                    className={styles.backButton}
                />
                {/* Show MediaGallery for posts with multiple media items or video */}
                {(hasMultipleMedia || (hasSingleMedia && hasVideoContent)) && currentPost.images ? (
                    <div className={styles.mediaGalleryContainer}>
                        <MediaGallery
                            items={currentPost.images}
                            height={400}
                            showPagination={hasMultipleMedia}
                        />
                    </div>
                ) : displayImageUrl ? (
                    <div
                        className={styles.heroImageContainer}
                        style={{ backgroundImage: `url(${displayImageUrl})` }}
                    >
                        <img
                            src={displayImageUrl}
                            alt={currentPost.title}
                            className={styles.heroImage}
                            onError={(e: any) => {
                                e.currentTarget.parentElement!.style.display = 'none';
                            }}
                        />
                    </div>
                ) : (
                    <div className={styles.heroPlaceholder}>
                        <img
                            src={elginLogo}
                            alt={t('Feed.ElginLogoAlt')}
                            className={styles.placeholderLogo}
                        />
                    </div>
                )}
                <div className={styles.postContent}>
                    <div className={styles.badges}>
                        {currentPost.category && (
                            <Label
                                size="large"
                                style={{
                                    backgroundColor:
                                        currentPost.category.color || '#005daa',
                                    color: getContrastTextColor(
                                        currentPost.category.color || '#005daa'
                                    ),
                                }}
                            >
                                {currentPost.category.displayName}
                            </Label>
                        )}
                        {currentPost.isUrgent && (
                            <Label color="red" size="large">
                                {t('Feed.Urgent')}
                            </Label>
                        )}
                    </div>
                    <h1 className={styles.postTitle}>{currentPost.title}</h1>
                    <div className={styles.postMeta}>
                        {currentPost.type === 'news' && (
                            <span>
                                {t('Feed.PublishedOn', {
                                    date: moment(
                                        currentPost.publishedAt
                                    ).format('MMMM D, YYYY'),
                                })}
                            </span>
                        )}
                        {currentPost.sourceName && (
                            <span> • {currentPost.sourceName}</span>
                        )}
                    </div>
                    {currentPost.type === 'event' && currentPost.eventDates && currentPost.eventDates.length > 0 && (
                        <div className={styles.eventDates}>
                            <h3>{t('Feed.EventDates')}</h3>
                            {[...currentPost.eventDates]
                                .sort((a, b) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime())
                                .map((eventDate) => (
                                    <div
                                        key={eventDate.id}
                                        className={`${styles.eventDate} ${
                                            eventDate.id === nextEventDate?.id
                                                ? styles.nextDate
                                                : ''
                                        }`}
                                    >
                                        <i className="calendar icon"></i>
                                        <span>
                                            {moment(eventDate.eventDate).format('MMMM D, YYYY')}
                                            {eventDate.startTime &&
                                                ` • ${eventDate.startTime}`}
                                            {eventDate.endTime &&
                                                ` - ${eventDate.endTime}`}
                                        </span>
                                        {eventDate.notes && (
                                            <span className={styles.eventNotes}>
                                                {eventDate.notes}
                                            </span>
                                        )}
                                    </div>
                                ))}
                        </div>
                    )}
                    {currentPost.type === 'event' && currentPost.eventLocation && (
                        <div className={styles.location}>
                            <h3>{t('Feed.Location')}</h3>
                            <p>
                                <i className="map marker icon"></i>
                                {currentPost.eventLocation}
                            </p>
                        </div>
                    )}
                    {currentPost.content && (
                        <div
                            className={styles.postBody}
                            dangerouslySetInnerHTML={{ __html: currentPost.content }}
                        />
                    )}
                    {currentPost.keywords && currentPost.keywords.length > 0 && (
                        <div className={styles.keywords}>
                            <h3>{t('Feed.Keywords')}</h3>
                            <div className={styles.keywordTags}>
                                {currentPost.keywords.map((keyword, index) => (
                                    <Label key={index} basic>
                                        {keyword}
                                    </Label>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className={styles.actions}>
                        <Button primary icon="share" content={t('Feed.Share')} onClick={handleShare} />
                    </div>
                    {relatedPosts && relatedPosts.length > 0 && (
                        <div className={styles.relatedPosts}>
                            <h2>
                                {currentPost.type === 'news'
                                    ? t('Feed.RelatedNews')
                                    : t('Feed.RelatedEvents')}
                            </h2>
                            <Grid stackable columns={3}>
                                {relatedPosts.map((post) => (
                                    <Grid.Column key={post.id}>
                                        <FeedCard post={post} />
                                    </Grid.Column>
                                ))}
                            </Grid>
                        </div>
                    )}
                </div>
            </Panel>
        </PageContainer>
    );
};
